import { memo, useMemo } from "react";
import { Background, BackgroundVariant, Controls, Handle, MiniMap, Position, ReactFlow, type Edge, type Node, type NodeProps } from "@xyflow/react";
import { Check, LockKeyhole } from "lucide-react";
import type { StepDefinition, StepState } from "../../shared/types";

interface GraphNodeData extends Record<string, unknown> {
  number: number;
  title: string;
  status: StepState["status"];
  progress: number;
  color: string;
}

const themeColors: Record<number, string> = {
  1: "#7ed7ff",
  2: "#ff8fb8",
  3: "#ffb66e",
  4: "#c8ff5a",
  5: "#d3b4ff",
  6: "#6de6c4",
};

const StepNode = memo(({ data, selected }: NodeProps<Node<GraphNodeData>>) => {
  const passed = data.status === "passed";
  const active = ["ready", "active", "evidence_pending", "gate_review", "reopened"].includes(data.status);
  return (
    <div className={`graph-node graph-${data.status} ${selected ? "graph-selected" : ""}`} style={{ "--node-color": data.color } as React.CSSProperties}>
      <Handle type="target" position={Position.Left} className="graph-handle" />
      <div className="graph-node-top">
        <span>{String(data.number).padStart(2, "0")}</span>
        {passed ? <Check size={11} /> : !active ? <LockKeyhole size={10} /> : <i />}
      </div>
      <strong>{data.title}</strong>
      <div className="graph-node-progress"><i style={{ width: `${data.progress}%` }} /></div>
      <Handle type="source" position={Position.Right} className="graph-handle" />
    </div>
  );
});

StepNode.displayName = "StepNode";

export function WorkflowGraph({
  steps,
  definitions,
  selectedStep,
  onSelect,
}: {
  steps: StepState[];
  definitions: StepDefinition[];
  selectedStep: number;
  onSelect: (number: number) => void;
}) {
  const nodes = useMemo<Node<GraphNodeData>[]>(
    () =>
      definitions.map((definition) => {
        const row = Math.floor((definition.number - 1) / 6);
        const rawColumn = (definition.number - 1) % 6;
        const column = row % 2 === 0 ? rawColumn : 5 - rawColumn;
        const state = steps.find((item) => item.number === definition.number)!;
        return {
          id: String(definition.number),
          type: "step",
          position: { x: column * 178, y: row * 118 },
          data: {
            number: definition.number,
            title: definition.shortTitle,
            status: state.status,
            progress: state.progress,
            color: themeColors[definition.theme],
          },
          selected: definition.number === selectedStep,
        };
      }),
    [definitions, selectedStep, steps],
  );

  const edges = useMemo<Edge[]>(
    () => {
      const pathEdges = definitions.slice(0, -1).map((definition) => {
        const sourceState = steps.find((item) => item.number === definition.number)!;
        const target = definitions[definition.number];
        const row = Math.floor((definition.number - 1) / 6);
        const crossesRow = definition.number % 6 === 0;
        return {
          id: `e-${definition.number}-${target.number}`,
          source: String(definition.number),
          target: String(target.number),
          type: crossesRow ? "smoothstep" : "default",
          animated: ["ready", "active", "evidence_pending", "gate_review"].includes(sourceState.status),
          style: {
            stroke: sourceState.status === "passed" ? "rgba(200,255,90,.65)" : "rgba(255,255,255,.13)",
            strokeWidth: sourceState.status === "passed" ? 1.8 : 1.1,
          },
        };
      });
      const regressionEdges = (definitions.find((definition) => definition.number === selectedStep)?.regressions ?? []).map((target) => ({
        id: `regression-${selectedStep}-${target}`,
        source: String(selectedStep),
        target: String(target),
        type: "smoothstep",
        animated: false,
        label: "revalidate",
        labelStyle: { fill: "rgba(255,182,110,.72)", fontSize: 7, fontFamily: "JetBrains Mono Variable" },
        style: { stroke: "rgba(255,182,110,.5)", strokeWidth: 1, strokeDasharray: "4 4" },
      } satisfies Edge));
      return [...pathEdges, ...regressionEdges];
    },
    [definitions, selectedStep, steps],
  );

  return (
    <div className="workflow-canvas" aria-label="Interactive 24-step venture path">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={{ step: StepNode }}
        onNodeClick={(_event, node) => onSelect(Number(node.id))}
        fitView
        fitViewOptions={{ padding: 0.12 }}
        minZoom={0.45}
        maxZoom={1.8}
        proOptions={{ hideAttribution: true }}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable
      >
        <Background variant={BackgroundVariant.Dots} gap={18} size={1} color="rgba(255,255,255,.07)" />
        <Controls showInteractive={false} position="bottom-right" />
        <MiniMap
          pannable
          zoomable
          nodeColor={(node) => String((node.data as GraphNodeData).color)}
          maskColor="rgba(5,8,10,.78)"
          position="bottom-left"
        />
      </ReactFlow>
    </div>
  );
}
